// FILE: planeClient.ts
// Purpose: HTTP wrapper around `/api/admin/plane/*`, mirroring SystemSettingsClient's
//          shape (plain object, static methods) over `adminFetch`.
// Layer: Web client
// Exports: `PlaneClient`
//
// Secrets travel one way only. Every method here can *send* a token or a
// password and none can read one back: the server answers with a status
// (`none` / `valid` / `invalid`) and, when a call has failed, the message Plane
// gave. That is what makes the forms safe to reload — there is nothing to
// rehydrate into a password field.

import type {
  AdminPlaneConnectionResponse,
  AdminPlaneProjectsResponse,
  AdminUserPlaneCredentialsResponse,
  AdminUserPlaneCredentialRow,
  PlaneConnectionUpdateRequest,
  PlaneConnectionView,
  PlaneProjectLinkUpdateRequest,
  PlaneProjectSummary,
  UserPlaneCredentialResponse,
  UserPlaneCredentialUpdateRequest,
  UserPlaneCredentialView,
} from "@taskin-code/contracts";

import { adminFetch } from "./adminFetch";

const CONNECTION_URL = "/api/admin/plane/connection";
const MY_CREDENTIAL_URL = "/api/plane/me/credential";
const ADMIN_CREDENTIALS_URL = "/api/admin/plane/credentials";
const PROJECTS_URL = "/api/admin/plane/projects";
const ISSUES_URL = (projectId: string) =>
  `/api/plane/projects/${encodeURIComponent(projectId)}/issues`;

/** One row of the "new thread from an issue" picker. */
export interface PlaneIssueSummary {
  readonly ref: string;
  readonly id: string;
  readonly title: string;
  readonly sequenceId: number;
  readonly stateName: string;
  readonly stateGroup: string;
  readonly priority: string | null;
}

interface PlaneProjectIssuesResponse {
  readonly planeProjectIdentifier: string;
  readonly issues: ReadonlyArray<PlaneIssueSummary>;
}

/** What the server echoes after a link is saved. Mirrors the project's Plane fields. */
export interface PlaneProjectLinkResult {
  readonly planeProjectId: string | null;
  readonly planeProjectIdentifier: string | null;
  readonly planeWorkspaceSlug: string | null;
}

export const PlaneClient = {
  /**
   * The installation's connection. Always answers — an unconfigured server
   * returns the defaults it would use, so the form opens filled in rather than
   * empty.
   */
  async readConnection(): Promise<PlaneConnectionView> {
    const result = await adminFetch<AdminPlaneConnectionResponse>(CONNECTION_URL);
    return result.planeConnection;
  },

  /**
   * Replaces the connection. Omit `token` to keep the stored one — an admin
   * fixing a workspace slug should not have to re-paste the credential — or send
   * an empty string to disconnect.
   */
  async writeConnection(value: PlaneConnectionUpdateRequest): Promise<PlaneConnectionView> {
    const result = await adminFetch<AdminPlaneConnectionResponse>(CONNECTION_URL, {
      method: "PUT",
      body: JSON.stringify(value),
    });
    return result.planeConnection;
  },

  /**
   * The boards of one workspace, for the link picker.
   *
   * Omitting `workspaceSlug` browses the installation's default. Passing one is
   * how a project gets linked to a board that lives in a different workspace of
   * the same Plane — the case that exists because one instance holds several.
   *
   * Fails with 409 when there is no admin token yet, which the screen turns into
   * "set the connection first" rather than a generic error.
   */
  async listProjects(workspaceSlug?: string): Promise<ReadonlyArray<PlaneProjectSummary>> {
    const query =
      workspaceSlug === undefined || workspaceSlug.trim() === ""
        ? ""
        : `?workspace=${encodeURIComponent(workspaceSlug.trim())}`;
    const result = await adminFetch<AdminPlaneProjectsResponse>(`${PROJECTS_URL}${query}`);
    return result.projects;
  },

  /**
   * The issues of a project's linked board, for the picker.
   *
   * Not an admin call: it answers for anyone who may open a thread in the
   * project. A project with no board answers 404, which the caller shows as
   * "this project is not linked" rather than as an error.
   */
  async listProjectIssues(projectId: string): Promise<PlaneProjectIssuesResponse> {
    return adminFetch<PlaneProjectIssuesResponse>(ISSUES_URL(projectId));
  },

  /**
   * The caller's own Plane credential — never anyone else's. There is no route
   * that reads or writes another person's, which is what keeps "posted by X"
   * meaning that X's account posted it.
   */
  async readMyCredential(): Promise<UserPlaneCredentialView> {
    const result = await adminFetch<UserPlaneCredentialResponse>(MY_CREDENTIAL_URL);
    return result.credential;
  },

  /**
   * Stores it, verifies it against Plane and mints the API token, in one call.
   *
   * Answers 200 even when Plane rejected the login: the row is stored either way
   * and the returned `status` / `lastError` are what the form shows. A rejected
   * password is a fact about the credential, not a failure of this request.
   */
  async writeMyCredential(
    value: UserPlaneCredentialUpdateRequest,
  ): Promise<UserPlaneCredentialView> {
    const result = await adminFetch<UserPlaneCredentialResponse>(MY_CREDENTIAL_URL, {
      method: "PUT",
      body: JSON.stringify(value),
    });
    return result.credential;
  },

  /** Forgets it here and revokes the minted token in Plane. */
  async deleteMyCredential(): Promise<UserPlaneCredentialView> {
    const result = await adminFetch<UserPlaneCredentialResponse>(MY_CREDENTIAL_URL, {
      method: "DELETE",
    });
    return result.credential;
  },

  /** Who has configured Plane and how it is doing. Statuses only, never a secret. */
  async listUserCredentials(): Promise<ReadonlyArray<AdminUserPlaneCredentialRow>> {
    const result = await adminFetch<AdminUserPlaneCredentialsResponse>(ADMIN_CREDENTIALS_URL);
    return result.credentials;
  },

  /** Links a Taskin project to a board — or unlinks it with `planeProjectId: null`. */
  async writeProjectLink(
    projectId: string,
    value: PlaneProjectLinkUpdateRequest,
  ): Promise<PlaneProjectLinkResult> {
    return adminFetch<PlaneProjectLinkResult>(`${PROJECTS_URL}/${encodeURIComponent(projectId)}`, {
      method: "PUT",
      body: JSON.stringify(value),
    });
  },
};
